import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';
import type { CustomField, CustomFieldInput } from './customFieldsService';

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = ENV.API_URL;

export const pmCustomFieldsApiService = {
  async list(params: { organization_id: string; project_id: string }): Promise<{ data: CustomField[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        project_id: params.project_id,
      });

      const response = await fetch(`${API_BASE_URL}/api/project-management/custom-fields?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch custom fields' };
      }

      return { data: (result.data || []) as CustomField[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async create(input: CustomFieldInput): Promise<{ data: CustomField | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/api/project-management/custom-fields`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to create custom field' };
      }

      return { data: result.data as CustomField, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async update(fieldId: string, organizationId: string, updates: Partial<CustomFieldInput>): Promise<{ data: CustomField | null; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(
        `${API_BASE_URL}/api/project-management/custom-fields/${encodeURIComponent(fieldId)}?${queryParams.toString()}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to update custom field' };
      }

      return { data: result.data as CustomField, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async delete(fieldId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(
        `${API_BASE_URL}/api/project-management/custom-fields/${encodeURIComponent(fieldId)}?${queryParams.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete custom field' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async reorder(params: { organization_id: string; project_id: string; field_ids: string[] }): Promise<{ error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/api/project-management/custom-fields/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to reorder custom fields' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
